import { ArrowLeft, ArrowRight } from "lucide-react";
import { createContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import Step1 from "./components/Step1";
import RightPanel from "./components/RightPanel";
import Step2 from "./components/Step2";
import Step3 from "./components/Step3";
import Step4 from "./components/Step4";
import Step5 from "./components/Step5";
import Step7 from "./components/Step7";
import Step8 from "./components/Step8";
import Step6 from "./components/Step6";

export const CreateJobContext = createContext<any>(null);

export const JOB_STEPS = [
  {
    label: "Requirement source",
    value: 1,
    tagline: "source and ownership",
    subTagline: "Choose the source and ownership.",
    title: "How would you like to begin?",
    subtitle:
      "Import an approved client requirement or create the job post manually.",
  },
  {
    label: "Role details",
    value: 2,
    tagline: "matching context first",
    subTagline: "Confirm classification and role facts.",
    title: "Set the role context",
    subtitle:
      "Classify the opportunity first, then confirm the public role facts and how the work is paid.",
  },
  {
    label: "Job content",
    value: 3,
    tagline: "published content",
    subTagline: "Write the candidate-facing post.",
    title: "Write the candidate-facing post",
    subtitle:
      "Explain the role clearly before defining the evidence COMTRACKED will match.",
  },
  {
    label: "Skills and experience",
    value: 4,
    tagline: "3 requirements",
    subTagline: "Define contextual matching evidence.",
    title: "Define the evidence required for this role",
    subtitle:
      "Every skill needs measurable criteria and a comment explaining how it will be used.",
  },
  {
    label: "Review job post",
    value: 5,
    tagline: "ready for settings",
    subTagline: "Check the complete candidate view.",
    title: "Review the complete job post",
    subtitle:
      "Check the public wording and the matching evidence candidates will see.",
  },
  {
    label: "Settings",
    value: 6,
    tagline: "match controls",
    subTagline: "Set match visibility and alerts.",
    title: "Settings",
    subtitle:
      "Control how matched candidates enter the agency review queue and when low candidate volume needs attention.",
  },
  {
    label: "Publication",
    value: 7,
    tagline: "agency controlled",
    subTagline: "Choose when post goes live.",
    title: "Publication timing",
    subtitle:
      "Choose when the approved job post becomes visible to candidates and begins matching.",
  },
  {
    label: "Payment",
    value: 8,
    tagline: "secure checkout",
    subTagline: "Authorise payment and publish.",
    title: "Pay for and publish this job post",
    subtitle:
      "Use available virtual wallet funds after completing the final order check.",
  },
];

const RenderBody = ({ activeStep }) => {
  switch (activeStep) {
    case 1:
      return <Step1 activeStep={activeStep} />;
    case 2:
      return <Step2 activeStep={activeStep} />;
    case 3:
      return <Step3 activeStep={activeStep} />;
    case 4:
      return <Step4 activeStep={activeStep} />;
    case 5:
      return <Step5 activeStep={activeStep} />;
    case 6:
      return <Step6 activeStep={activeStep} />;
    case 7:
      return <Step7 activeStep={activeStep} />;
    case 8:
      return <Step8 activeStep={activeStep} />;
    default:
      return <Step1 activeStep={activeStep} />;
  }
};

const CreateJobPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(6);

  return (
    <CreateJobContext.Provider
      value={{
        activeStep,
        setActiveStep,
      }}
    >
      <div className="flex flex-1 min-h-0 h-full gap-4">
        {/* Left section */}
        <div className="flex flex-col flex-1 min-h-0 gap-3">
          {/* Header */}
          <div className="flex items-center rounded-md bg-white overflow-hidden border">
            <button
              onClick={() => navigate("/job/by-job")}
              className="group flex items-center gap-2 h-full pl-3.5 pr-10"
            >
              <span className="rounded-full p-1 border text-content group-hover:bg-slate-600 group-hover:text-white">
                <ArrowLeft size={13} />
              </span>
              <span className="font-semibold text-xs">Jobs</span>
            </button>

            <div className="flex items-center justify-between flex-1 pr-5 py-3.5">
              <div className="flex flex-col border-l pl-5 gap-0.5">
                <span className="text-xss font-bold text-light-black uppercase">
                  Recruitment workspace / Create job
                </span>
                <span className="font-semibold text-xl">
                  Create a new job post
                </span>
                <span className="text-xss font-medium text-light-black">
                  Build the published role and its matching requirements before
                  payment.
                </span>
              </div>

              <button className="rounded-md border px-4 py-2 font-semibold text-xs hover:bg-gray-50">
                Save draft
              </button>
            </div>
          </div>

          {/* Main body */}
          <div className="bg-white flex flex-col flex-1 rounded-md border min-h-0 overflow-hidden">
            <div className="p-5 min-h-0 overflow-y-scroll no-scrollbar flex-1">
              <RenderBody activeStep={activeStep} />
            </div>
            {/* Footer Section */}
            <div className="flex items-center justify-between bg-slate-50 border-t px-3.5 py-2.5">
              <button
                onClick={() => {
                  if (activeStep === 1) {
                    navigate(-1);
                  } else {
                    setActiveStep((prev) => prev - 1);
                  }
                }}
                className="rounded-md bg-white border text-content-muted h-8 font-semibold text-xss px-8 hover:bg-gray-100"
              >
                {activeStep === 1 ? "Cancel" : "Back"}
              </button>
              <span className="text-xss font-medium text-light-black">
                Import the selected requirement or choose a manual draft.
              </span>
              {activeStep !== JOB_STEPS?.length ? (
                <button
                  onClick={() => {
                    setActiveStep((prev) => prev + 1);
                  }}
                  className="flex items-center gap-3 rounded-md bg-black h-8 text-white font-semibold text-xss px-4"
                >
                  Continue
                  <ArrowRight size={14} />
                </button>
              ) : <div></div>}
            </div>
          </div>
        </div>
        {/* Right section */}
        <RightPanel />
      </div>
    </CreateJobContext.Provider>
  );
};

export default CreateJobPage;
